/**
 * Mounts the consent UI into <div id="tune-consent-root"> with an open Shadow
 * DOM so its CSS neither leaks into nor inherits from the host page (landing
 * CSS, Bootstrap, app Tailwind with important:true). Exposes
 * window.TuneConsent (and the alias window.tuneConsent).
 */
import { render } from 'solid-js/web'
import ConsentRoot from './ConsentRoot'
import { clearStoredConsent, reloadPage, getConsent, getConsentId, initCore, subscribe, type ConsentSurface, type StoredConsent } from './consent-store'
import { postConsent, retryPendingConsent, syncConsentAfterLogin, type SyncOutcome } from './consent-api'
import { initClickToLoadEmbeds } from './youtube'

export interface TuneConsentApi {
  /** open the preferences modal (with saved values). `trigger` gets focus back on close. */
  open: (source?: ConsentSurface, trigger?: Element | null) => void
  /**
   * CCPA "Do Not Sell or Share My Personal Information": store marketing=false
   * (analytics unchanged) through the same withdrawal path as GPC, then show the
   * saved notice. Returns false (and shows the save-error notice) if it could not be stored.
   */
  doNotSell: (source?: ConsentSurface) => boolean
  /** after login: merge server consent state with the local one (see consent-api.ts) */
  syncAfterLogin: (authHeaders?: Record<string, string>) => Promise<SyncOutcome>
  get: () => StoredConsent | null
  /** uuid sent as X-Consent-Id on login; null before any choice */
  getConsentId: () => string | null
  onChange: (fn: (s: StoredConsent | null) => void) => () => void
  /** support/testing: forget the local choice, then reload the page (the banner shows again and any running GA4/GTM is gone) */
  reset: () => void
}

declare global {
  interface Window {
    TuneConsent?: TuneConsentApi
    tuneConsent?: TuneConsentApi
  }
}

export function mountConsent(opts: { css?: string; postToServer?: boolean } = {}): TuneConsentApi {
  if (window.TuneConsent) return window.TuneConsent
  const stored = initCore()
  if (opts.postToServer !== false) retryPendingConsent().catch(() => {})

  const host = document.createElement('div')
  host.id = 'tune-consent-root'
  document.body.appendChild(host)
  const shadow = host.attachShadow({ mode: 'open' })
  if (opts.css) {
    const style = document.createElement('style')
    style.textContent = opts.css
    shadow.appendChild(style)
  }
  const container = document.createElement('div')
  shadow.appendChild(container)

  let openFn: (s?: ConsentSurface, trigger?: Element | null) => void = () => {}
  let doNotSellFn: (s?: ConsentSurface) => boolean = () => false
  render(
    () => (
      <ConsentRoot
        root={shadow}
        initiallyOpen={!stored}
        onSave={opts.postToServer === false ? undefined : postConsent}
        registerOpen={(f) => (openFn = f)}
        registerDoNotSell={(f) => (doNotSellFn = f)}
      />
    ),
    container
  )

  const api: TuneConsentApi = {
    open: (s, trigger) => openFn(s, trigger ?? (document.activeElement !== document.body ? document.activeElement : null)),
    doNotSell: (s = 'settings_link') => doNotSellFn(s),
    syncAfterLogin: (h) => syncConsentAfterLogin(h),
    get: getConsent,
    getConsentId,
    onChange: subscribe,
    reset: () => {
      clearStoredConsent()
      reloadPage()
    },
  }
  window.TuneConsent = api
  window.tuneConsent = api

  // Any element with data-tune-consent-open (footer "Cookie settings" links) opens the modal;
  // data-tune-consent-dns (footer "Do Not Sell or Share" links) sets marketing off.
  document.addEventListener('click', (e) => {
    const el = e.target as Element | null
    const t = el?.closest?.('[data-tune-consent-open]')
    if (t) {
      e.preventDefault()
      api.open('settings_link', t)
      return
    }
    const d = el?.closest?.('[data-tune-consent-dns]')
    if (d) {
      e.preventDefault()
      api.doNotSell('settings_link')
    }
  })
  initClickToLoadEmbeds(document)
  return api
}
